/**
 * The action bar for a proposal.
 *
 * Driven by `available_actions`, which the API derives from the *same* transition table it
 * enforces. That is the whole reason it exists: a button that renders and then 403s is a bug
 * this design cannot have, because the list of buttons and the list of permitted transitions
 * are one list.
 *
 * `expected_version` rides on every call. A mismatch is `409 stale_proposal`, which this turns
 * into "reload and look at what changed" rather than an overwrite of a decision nobody saw.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, ApiError } from '@/lib/api';
import { ACTION_LABEL } from '@/lib/policy';
import { Alert, Button, Dialog, Field, Textarea } from '@/components/ui';
import { ErrorNote } from '@/components/bits';
import type { Proposal, ProposalAction } from '@/lib/types';

interface Spec {
  /** Prompt shown in the dialog. Absent means the action needs no input at all. */
  prompt?: string;
  /** Label above the text box. */
  field?: string;
  required?: boolean;
  variant?: 'primary' | 'secondary' | 'destructive';
  run: (
    proposal: Proposal,
    note: string,
  ) => Promise<{ status?: string; released_cents?: number | null }>;
}

const SPECS: Partial<Record<ProposalAction, Spec>> = {
  withdraw: {
    prompt:
      'Withdrawing takes this out of review. Nothing has been reserved yet, so nothing is released.',
    field: 'Reason (optional)',
    run: (proposal, note) =>
      api.withdrawProposal(proposal.id, {
        ...(note.trim() ? { reason: note.trim() } : {}),
        expected_version: proposal.version,
      }),
  },
  sponsor_endorse: {
    prompt: 'Endorsing sends this to HR to confirm the budget.',
    field: 'Note (optional)',
    variant: 'primary',
    run: (proposal, note) =>
      api.proposalSponsorAction(proposal.id, {
        action: 'endorse',
        ...(note.trim() ? { note: note.trim() } : {}),
        expected_version: proposal.version,
      }),
  },
  sponsor_return: {
    prompt: 'The manager sees this reason, so say what needs changing.',
    field: 'What needs changing',
    required: true,
    run: (proposal, note) =>
      api.proposalSponsorAction(proposal.id, {
        action: 'return',
        note: note.trim(),
        expected_version: proposal.version,
      }),
  },
  sponsor_reject: {
    prompt: 'Rejecting ends this proposal. Returning it for changes is usually kinder.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (proposal, note) =>
      api.proposalSponsorAction(proposal.id, {
        action: 'reject',
        note: note.trim(),
        expected_version: proposal.version,
      }),
  },
  hr_return: {
    prompt: 'The manager sees this reason and can edit and resubmit.',
    field: 'What needs changing',
    required: true,
    run: (proposal, note) =>
      api.returnProposal(proposal.id, { reason: note.trim(), expected_version: proposal.version }),
  },
  reject: {
    prompt: 'Rejecting ends this proposal.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (proposal, note) =>
      api.rejectProposal(proposal.id, { reason: note.trim(), expected_version: proposal.version }),
  },
  request_clarification: {
    prompt: 'This parks the proposal with whoever must answer, and returns it to you when they do.',
    field: 'Your question',
    required: true,
    run: (proposal, note) =>
      api.askProposalClarification(proposal.id, {
        body: note.trim(),
        expected_version: proposal.version,
      }),
  },
  cancel: {
    prompt: 'Cancelling a confirmed event releases every reservation it holds.',
    field: 'Reason',
    required: true,
    variant: 'destructive',
    run: (proposal, note) =>
      api.cancelProposal(proposal.id, { reason: note.trim(), expected_version: proposal.version }),
  },
  reopen: {
    prompt: 'Reopening releases the reservations and sends this back to HR review.',
    field: 'Reason',
    required: true,
    run: (proposal, note) =>
      api.reopenProposal(proposal.id, { reason: note.trim(), expected_version: proposal.version }),
  },
  expire: {
    prompt:
      'Closing out releases the reservations. Until you do, an overdue event keeps holding its budget (D-36) — that is deliberate, so nothing is quietly written off.',
    field: 'Note (optional)',
    variant: 'destructive',
    run: (proposal, note) =>
      api.expireProposal(proposal.id, {
        ...(note.trim() ? { reason: note.trim() } : {}),
        expected_version: proposal.version,
      }),
  },
};

/** Actions handled elsewhere, because they need a screen rather than a text box. */
const ELSEWHERE: ProposalAction[] = [
  'submit',
  'amend_attendees',
  'answer_clarification',
  'confirm',
];

export function ProposalActions({
  proposal,
  onConfirm,
  onAmend,
}: {
  proposal: Proposal;
  onConfirm?: () => void;
  onAmend?: () => void;
}) {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<ProposalAction | null>(null);
  const [note, setNote] = useState('');

  const spec = open ? SPECS[open] : undefined;

  const act = useMutation({
    mutationFn: async () => {
      if (!open || !spec) throw new Error('no action selected');
      return spec.run(proposal, note);
    },
    onSuccess: async (result) => {
      setOpen(null);
      setNote('');
      await cache.invalidateQueries({ queryKey: ['proposal', proposal.id] });
      await cache.invalidateQueries({ queryKey: ['proposals'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
      if (result.released_cents) {
        // Every screen showing a pool is now wrong until it refetches.
        await cache.invalidateQueries({ queryKey: ['calculate-roster'] });
        await cache.invalidateQueries({ queryKey: ['calculate-selection'] });
      }
    },
  });

  const buttons = proposal.available_actions.filter((action) => !ELSEWHERE.includes(action));
  const canEdit = proposal.available_actions.includes('submit');
  const canConfirm = proposal.available_actions.includes('confirm');
  const canAmend = proposal.available_actions.includes('amend_attendees');
  const canClaim =
    proposal.status === 'confirmed' ||
    proposal.status === 'claim_due' ||
    proposal.status === 'claim_overdue';

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {canEdit ? (
          <Button variant="primary" onClick={() => navigate(`/proposals/${proposal.id}/edit`)}>
            Edit and submit
          </Button>
        ) : null}
        {canClaim ? (
          <Button variant="primary" onClick={() => navigate(`/claims/new?proposal=${proposal.id}`)}>
            Raise claim
          </Button>
        ) : null}
        {canConfirm && onConfirm ? (
          <Button variant="primary" onClick={onConfirm}>
            {ACTION_LABEL.confirm}
          </Button>
        ) : null}
        {canAmend && onAmend ? (
          <Button onClick={onAmend}>{ACTION_LABEL.amend_attendees}</Button>
        ) : null}
        {buttons.map((action) => (
          <Button
            key={action}
            variant={SPECS[action]?.variant === 'destructive' ? 'destructive' : 'secondary'}
            onClick={() => {
              setNote('');
              setOpen(action);
            }}
          >
            {ACTION_LABEL[action] ?? action}
          </Button>
        ))}
      </div>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? (ACTION_LABEL[open] ?? open) : ''}
        footer={
          <>
            <Button onClick={() => setOpen(null)}>Cancel</Button>
            <Button
              variant={spec?.variant === 'destructive' ? 'destructive' : 'primary'}
              busy={act.isPending}
              disabled={spec?.required === true && note.trim() === ''}
              onClick={() => act.mutate()}
            >
              {open ? (ACTION_LABEL[open] ?? 'Confirm') : 'Confirm'}
            </Button>
          </>
        }
      >
        {spec?.prompt ? <p className="text-muted-foreground">{spec.prompt}</p> : null}
        {spec?.field ? (
          <Field label={spec.field} required={spec.required}>
            {(props) => (
              <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />
            )}
          </Field>
        ) : null}
        {act.error instanceof ApiError && act.error.isStale ? (
          <Alert tone="warn" title="This proposal changed while you were looking at it">
            Someone else acted on it. Reload the page and check what changed before acting.
          </Alert>
        ) : act.error ? (
          <ErrorNote error={act.error} />
        ) : null}
      </Dialog>
    </>
  );
}
